import { computed, ref, watch } from 'vue';
import { useDisplayLogic } from '@rebuild/materials/shared/page_container/useDisplayLogic';
import { useModuleAttrs } from '@rebuild/hooks/useModuleAttrs';
import {
	checkChildrenMenus,
	checkChildrenModules,
	checkModuleActive,
	checkMenuActive,
	isOverviewMenu
} from './useNavMenu';

/**
 * 抽屉菜单项逻辑 Hook
 * @param {ComputedRef} currentMenu - 当前菜单对象（computed 计算属性）
 * @returns {Object} 返回所有可复用的状态、计算属性和方法
 */
export function useDrawerMenuItem(currentMenu) {
	// 创建 sourceMenu computed 属性，转发 currentMenu 的值
	const sourceMenu = computed(() => currentMenu.value);

	const { displayMenu, displayModule } = useDisplayLogic();
	const { isTextImageM } = useModuleAttrs();

	// 状态计算
	const hasSubMenus = computed(() => checkChildrenMenus(sourceMenu.value));
	const hasModules = computed(() => checkChildrenModules(sourceMenu.value));
	const hasDropdownContent = computed(() => {
		if (isOverviewMenu(sourceMenu.value)) return false;
		return hasSubMenus.value || hasModules.value;
	});

	// 部分模块没有详情视图，过滤展示
	const enableShowModule = module => {
		const { moduleId } = module;

		if (moduleId === 'new_banner') return false;
		if (moduleId === 'contact_information') return false;
		if (isTextImageM(module)) return false;

		return true;
	};

	// ############ 选中状态 Start #############
	// 判断模块是否选中
	const isModuleActive = module => {
		return checkModuleActive(module, displayModule.value, {
			parentMenuId: sourceMenu.value.uuid,
			currentActiveMenuId: displayMenu.value.uuid
		});
	};

	// 判断菜单是否选中
	const isMenuActive = computed(() => {
		return checkMenuActive(sourceMenu.value, displayMenu.value, displayModule.value);
	});

	// 检查子菜单中是否有激活的菜单或模块
	const hasActiveChild = computed(() => {
		if (!hasSubMenus.value) return false;

		// 递归检查子菜单
		const checkMenu = menu => {
			// 检查菜单本身是否激活
			if (displayMenu.value?.uuid === menu.uuid) {
				return true;
			}
			// 检查菜单的模块是否激活
			if (menu.moduleVOS && Array.isArray(menu.moduleVOS)) {
				if (menu.moduleVOS.some(module => isModuleActive(module))) {
					return true;
				}
			}
			// 递归检查子菜单
			if (menu.pageMenuVOS && Array.isArray(menu.pageMenuVOS)) {
				return menu.pageMenuVOS.some(subMenu => checkMenu(subMenu));
			}
			return false;
		};

		return sourceMenu.value.pageMenuVOS.some(subMenu => checkMenu(subMenu));
	});
	// ############ 选中状态 End #############

	// ############ 内容展开 Start #############
	// 判断是否需要默认展开
	const shouldExpand = computed(() => {
		// 如果当前菜单激活，需要展开
		if (isMenuActive.value) {
			return true;
		}
		// 如果当前菜单有模块激活，需要展开
		if (hasModules.value && sourceMenu.value.moduleVOS.some(module => isModuleActive(module))) {
			return true;
		}
		// 如果子菜单中有激活的菜单或模块，需要展开
		if (hasActiveChild.value) {
			return true;
		}
		return false;
	});

	const expanded = ref(false);

	// 根据激活状态自动展开
	watch(
		shouldExpand,
		newVal => {
			if (newVal && hasDropdownContent.value) {
				expanded.value = true;
			}
		},
		{ immediate: true }
	);

	const handleToggleExpand = () => {
		expanded.value = !expanded.value;
	};
	// ############ 内容展开 End #############

	return {
		hasSubMenus,
		hasModules,
		hasDropdownContent,
		enableShowModule,
		isModuleActive,
		isMenuActive,
		expanded,
		handleToggleExpand
	};
}
